// Multi-tenant scoped reads — ONE door for the shopId filter so no read can forget it.
// SAFE-BY-DEFAULT: if the active shop isn't resolved yet, scopedQuery returns the query UNFILTERED
// (never filters on a null id → never blanks a report). Single-shop today, so a scoped read returns the
// exact same rows as before; the win is that shop #2 can't cross-contaminate.
// BEHAVIOR-PIN: project-slik-production-safety-net-2026-09-11 (multi-tenant Step 4).
import {
  query, where, collection, getDocs,
  type Query, type QueryConstraint, type CollectionReference, type DocumentData,
} from 'firebase/firestore';
import { db } from '../config/firebase';

let _activeShopId: string | null = null;
let _resolving: Promise<string | null> | null = null;

/** ShopContext pushes the resolved shop id here so the singleton services (no React context) can scope. */
export function setActiveShopId(id: string | null): void { _activeShopId = id || null; }
export function getActiveShopId(): string | null { return _activeShopId; }

/**
 * Ensure the active shop id is resolved. If ShopContext hasn't set it yet, lazily read the single shops
 * doc (single-shop safe). Reads should `await ensureActiveShopId()` before building a scoped query so the
 * filter is present. Returns null only if there is no shop at all (then scopedQuery stays unfiltered).
 */
export async function ensureActiveShopId(): Promise<string | null> {
  if (_activeShopId) return _activeShopId;
  if (!_resolving) {
    _resolving = getDocs(collection(db, 'shops'))
      .then((s) => { _activeShopId = s.empty ? null : s.docs[0].id; return _activeShopId; })
      .catch(() => null);
  }
  return _resolving;
}

/**
 * Build a query scoped to the active shop: prepends `where('shopId','==',activeShopId)`. If no shop is
 * resolved, returns the query with the given constraints only (UNFILTERED) — single-shop safe, never
 * returns zero rows by filtering on a null id.
 */
export function scopedQuery(
  collRef: CollectionReference<DocumentData>,
  ...constraints: QueryConstraint[]
): Query<DocumentData> {
  const sid = _activeShopId;
  return sid
    ? query(collRef, where('shopId', '==', sid), ...constraints)
    : query(collRef, ...constraints);
}
